import { useEffect } from "react";

/**
 * Leaves the browser time to move the focus (to the next cell) and the
 * keyboard time to open or close before measuring.
 */
export const KEYBOARD_SETTLE_DELAY_MS = 100;

/**
 * Below this, a shorter visual viewport is the browser toolbars moving, not
 * an on-screen keyboard.
 */
export const KEYBOARD_MIN_HEIGHT_PX = 120;

/**
 * A pinch zoom shrinks the visual viewport too: above this scale the shorter
 * viewport is the zoom, and the shell keeps its height.
 */
export const MAX_UNZOOMED_SCALE = 1.05;

/** Height of the shell while the keyboard is open (read by App.scss). */
export const VISIBLE_HEIGHT_VAR = "--app-visible-height";

const holdsKeyboard = (element: Element | null): element is HTMLElement =>
  element instanceof HTMLElement &&
  (element.isContentEditable || element.matches("input, textarea, select"));

/**
 * The authenticated shell never scrolls the document: `.App` is exactly the
 * visible height and `main` scrolls inside it. iOS Safari does not shrink
 * that height for the on-screen keyboard: it scrolls the whole document to
 * lift the focused field, uncovering the empty space under the app (a black
 * band above the keyboard), and leaves it there once the keyboard closes.
 *
 * So while a field holds the keyboard, the shell takes the height the
 * keyboard leaves visible and the document stays put: `main` brings the
 * field into view by itself. Once the keyboard is gone, the shell goes back
 * to `100dvh` and the document to the top.
 */
const useKeyboardScrollReset = () => {
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;
    let timer: number | undefined;

    const resetDocumentScroll = () => {
      if (window.scrollX !== 0 || window.scrollY !== 0) {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    };

    const fit = () => {
      const field = document.activeElement;
      const hiddenHeight = viewport ? root.clientHeight - viewport.height : 0;

      if (
        viewport &&
        viewport.scale <= MAX_UNZOOMED_SCALE &&
        holdsKeyboard(field) &&
        hiddenHeight > KEYBOARD_MIN_HEIGHT_PX
      ) {
        root.style.setProperty(VISIBLE_HEIGHT_VAR, `${viewport.height}px`);
        resetDocumentScroll();
        field.scrollIntoView({ block: "nearest" });
        return;
      }

      root.style.removeProperty(VISIBLE_HEIGHT_VAR);
      if (!holdsKeyboard(field)) {
        resetDocumentScroll();
      }
    };

    const fitLater = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(fit, KEYBOARD_SETTLE_DELAY_MS);
    };

    document.addEventListener("focusin", fitLater);
    document.addEventListener("focusout", fitLater);
    // The keyboard opening or closing resizes the visual viewport; iOS
    // lifting the field scrolls it.
    viewport?.addEventListener("resize", fit);
    viewport?.addEventListener("scroll", fit);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("focusin", fitLater);
      document.removeEventListener("focusout", fitLater);
      viewport?.removeEventListener("resize", fit);
      viewport?.removeEventListener("scroll", fit);
      root.style.removeProperty(VISIBLE_HEIGHT_VAR);
    };
  }, []);
};

export default useKeyboardScrollReset;
